import { BulkUploadWizardPageObject } from '@/poms/bulkUploadWizardPageObjectModel';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { BulkUploadData, UpdateResult, WizardStep } from '../types';
import { AssetMatchingStep } from './steps/AssetMatchingStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { DataPreviewStep } from './steps/DataPreviewStep';
import { FieldTypesStep } from './steps/FieldTypesStep';
import { FileUploadStep } from './steps/FileUploadStep';
import { ProcessingStep } from './steps/ProcessingStep';
import { ResultsStep } from './steps/ResultsStep';

const STEPS = [
  { key: WizardStep.FILE_UPLOAD, label: 'Upload File' },
  { key: WizardStep.DATA_PREVIEW, label: 'Preview Data' },
  { key: WizardStep.FIELD_TYPES, label: 'Set Data Types' },
  { key: WizardStep.ASSET_MATCHING, label: 'Match Assets' },
  { key: WizardStep.CONFIRMATION, label: 'Confirm Changes' },
  { key: WizardStep.PROCESSING, label: 'Apply Data' },
  { key: WizardStep.RESULTS, label: 'Results' },
];

export const BulkUploadWizard = ({
  currentStep,
  bulkUploadData,
  onStepChange,
  onDataChange,
  onReset,
}: {
  currentStep: WizardStep;
  bulkUploadData?: BulkUploadData;
  onStepChange: (step: WizardStep) => void;
  onDataChange: (data: BulkUploadData) => void;
  onReset: () => void;
}) => {
  const activeStepIndex = STEPS.findIndex(step => step.key === currentStep);

  const renderStepContent = () => {
    switch (currentStep) {
      case WizardStep.FILE_UPLOAD: {
        return (
          <FileUploadStep
            onFileUploaded={data => {
              onDataChange(data);
              onStepChange(WizardStep.DATA_PREVIEW);
            }}
          />
        );
      }
      case WizardStep.DATA_PREVIEW: {
        return (
          <DataPreviewStep
            data={bulkUploadData}
            onNext={() => onStepChange(WizardStep.FIELD_TYPES)}
            onBack={() => onStepChange(WizardStep.FILE_UPLOAD)}
            onDataChange={onDataChange}
          />
        );
      }
      case WizardStep.FIELD_TYPES: {
        return (
          <FieldTypesStep
            data={bulkUploadData}
            onNext={() => onStepChange(WizardStep.ASSET_MATCHING)}
            onBack={() => onStepChange(WizardStep.DATA_PREVIEW)}
            onDataChange={onDataChange}
          />
        );
      }
      case WizardStep.ASSET_MATCHING: {
        return (
          <AssetMatchingStep
            data={bulkUploadData}
            onNext={() => onStepChange(WizardStep.CONFIRMATION)}
            onBack={() => onStepChange(WizardStep.FIELD_TYPES)}
            onDataChange={onDataChange}
          />
        );
      }
      case WizardStep.CONFIRMATION: {
        return (
          <ConfirmationStep
            data={bulkUploadData}
            onNext={() => onStepChange(WizardStep.PROCESSING)}
            onBack={() => onStepChange(WizardStep.ASSET_MATCHING)}
          />
        );
      }
      case WizardStep.PROCESSING: {
        return (
          <ProcessingStep
            data={bulkUploadData}
            onComplete={(results: UpdateResult[]) => {
              if (bulkUploadData) {
                const processedAssetNames = new Set(results.map(r => r.assetName));
                const skippedAssets = bulkUploadData.parsedData
                  .filter(row => !processedAssetNames.has(row.assetName))
                  .map(row => row.assetName);
                const updatedData = {
                  ...bulkUploadData,
                  results: {
                    totalProcessed: results.length,
                    successful: results.filter(r => r.status === 'success'),
                    failed: results.filter(r => r.status === 'error'),
                    skipped: skippedAssets,
                  },
                };
                onDataChange(updatedData);
              }
              onStepChange(WizardStep.RESULTS);
            }}
          />
        );
      }
      case WizardStep.RESULTS: {
        return <ResultsStep data={bulkUploadData} onReset={onReset} />;
      }
      default: {
        return;
      }
    }
  };

  return (
    <Box
      data-testid={BulkUploadWizardPageObject.WizardContainer}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stepper
        data-testid={BulkUploadWizardPageObject.WizardStepper}
        activeStep={activeStepIndex}
        sx={{
          marginBottom: '32px',
          flexShrink: 0,
        }}
        alternativeLabel
      >
        {STEPS.map(step => (
          <Step key={step.key} data-testid={`step-${step.key}`}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box
        data-testid={BulkUploadWizardPageObject.StepContent}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {renderStepContent()}
      </Box>
    </Box>
  );
};
